import { noop } from 'lodash';
import cn from 'classnames';
import React, { useState } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';
import { SLIDER } from '../../propTypes';
import { DIRECTIONS, SWIPE_THRESHOLD, SWIPE_ANGLE_DEG_THRESHOLD } from './constants';
import './index.scss';

/**
 * Due to the fact that TransitionGroup won't update unmounting children, This factory is needed in order
 * to pass on the proper animation by prev/next
 * @param {Boolean} next - Whether item should have next / prev animation
 * @see http://reactcommunity.org/react-transition-group/transition-group#TransitionGroup-prop-childFactory
 * return {[]} A cloned react children array
 */
const childFactory = (next) => (child) =>
    React.cloneElement(child, {
        classNames: {
            enter: next ? 'enter-next' : 'enter-prev',
            enterActive: 'enter-active',
            exit: 'leave',
            exitActive: next ? 'leave-active-next' : 'leave-active-prev',
        },
    });

const Slider = ({
    className,
    slides,
    initialIndex = 0,
    onSlideChange = noop,
    Component,
    onSlideClick = noop,
    isMobile = false,
    setDirection = noop,
}) => {
    const [activeIndex, setActiveIndex] = useState(initialIndex);
    const [isNext, setIsNext] = useState(false);
    let touchStartX = 0,
        touchStartY = 0;

    const handleSlideChange = (direction) => {
        let newIndex = activeIndex + direction;

        if (newIndex < 0) {
            // When reached negative, sets to the last item
            newIndex = slides.length - 1;
        } else if (newIndex >= slides.length) {
            // When exceeding slides length, sets to the first item
            newIndex = 0;
        }

        setActiveIndex(newIndex);
        setIsNext(direction === DIRECTIONS.NEXT);

        onSlideChange(newIndex);
    };

    const handleArrowClick = (direction) => (e) => {
        setDirection(direction);
        e.preventDefault();

        handleSlideChange(direction);
    };

    const factory = childFactory(isNext);

    const currentSlide = slides[activeIndex];

    const handleTouchStart = (e) => {
        const { changedTouches = [] } = e;
        const [{ clientX, clientY }] = changedTouches;

        touchStartX = clientX;
        touchStartY = clientY;

        e.stopPropagation();
    };

    const handleTouchEnd = (e) => {
        const { changedTouches = [] } = e;
        const [{ clientX, clientY }] = changedTouches;
        const dX = Math.abs(touchStartX - clientX);
        const dY = Math.abs(touchStartY - clientY);
        const swipeAngle = (Math.atan(dY / dX) * 180) / Math.PI;

        if (dX >= SWIPE_THRESHOLD && swipeAngle < SWIPE_ANGLE_DEG_THRESHOLD) {
            const direction = touchStartX > clientX ? DIRECTIONS.NEXT : DIRECTIONS.BACK;
            setDirection(direction);
            handleSlideChange(direction);
        }
        e.stopPropagation();
    };

    const touchHandlers = {
        onTouchStart: handleTouchStart,
        onTouchEnd: handleTouchEnd,
    };

    const handleVariationClick = (param) => {
        onSlideClick(param);
    };

    return (
        <div className={cn(className, 'logo-slider-wrapper')}>
            <div className="arrow-wrapper back" onClick={handleArrowClick(DIRECTIONS.BACK)}>
                <button className="arrow back" />
            </div>
            <div className="logo-slider" {...(isMobile && { ...touchHandlers })}>
                <TransitionGroup component={null} childFactory={factory}>
                    <CSSTransition key={currentSlide.id} timeout={500} unmountOnExit>
                        <div className="slide">
                            <Component {...currentSlide} onClick={handleVariationClick} />
                        </div>
                    </CSSTransition>
                </TransitionGroup>
            </div>
            <div className="arrow-wrapper next" onClick={handleArrowClick(DIRECTIONS.NEXT)}>
                <button className="arrow next" />
            </div>
        </div>
    );
};

Slider.propTypes = SLIDER;

export default Slider;
